/**
 * class will handle the api calls.
 * @author Ravi Kumar sha
 * @email ravi.sha@nagarro.com
 */

import axios from 'axios';
import moment from 'moment';
import router from '@/router';
import store from '@/store/index';
import { GetPreferredLanguage, getUrlQueryParam, getAllUrlQueryParam } from '@/utils/helper';
import Constants from '@/utils/constants';
class Fetch {
    constructor() {
        this.observer = [];
        this.showLoader = true;
    }

    addNewUrl(url) {
        this.observer.push(url);
    }

    addApplicationHeaders(auth) {
        const headersLang = GetPreferredLanguage();
        if(auth && auth.headers) {
            auth.headers['Accept-Language'] = headersLang;
            auth.headers['ApplicationId'] = Constants.ApplicationId;
            auth.headers['IANATimezone'] = moment.tz.guess();
        } else {
            auth = {
                headers : {
                    'Accept-Language': headersLang,
                    'ApplicationId': Constants.ApplicationId,
                    'IANATimezone': moment.tz.guess(),
                }
            };
        }
        return auth;
    }

    /**
     * Method tells any request is pending or not
     */
    isPendingRequest() {
        return !!this.observer.length;
    }

    removeOldUrl(url) {
        this.observer.splice(
            this.observer.indexOf(url),
            1
        );
    }

    queryParamsBuilder(queryParams) {
        const keys = Object.keys(queryParams);
        let query = '?';

        if (!keys) {
            return '';
        }

        keys.forEach(key => query += `${key}=${queryParams[key]}&`);
        return query.slice(0, -1);
    }

    argumentMapper(params) {
        const [method, url, auth, addUrlToWatchList ] = params;
        return {
            method,
            url,
            param: (params.length === 7) ? params[4] : null,
            auth,
            addUrlToWatchList,
            reject: params[params.length - 1],
            resolve: params[params.length - 2]
        }
    }

    ajax() {
        const { method, url, param, auth, addUrlToWatchList, resolve, reject } = this.argumentMapper(arguments);
        if(addUrlToWatchList) {
            this.addNewUrl(url);
        }
        axios[method](url, param, auth).then(response => {
            this.removeOldUrl(url);
            resolve(response);
        }, error => {
            this.removeOldUrl(url);
            if(error.response !==undefined && error.response.status === 401){
                const uniqueId = store.getters['conferenceCall/GET_UNIQUE_ID'];
                if(uniqueId) {
                    router.push({
                        name: 'login-new',
                        params: {
                            uniqueId: uniqueId
                        }
                    });
                } else {
                    const showId = getUrlQueryParam();
                    const confId = getAllUrlQueryParam('confId');
                    if(confId) {
                        const email = getAllUrlQueryParam('email');
                        if(email){
                            router.push(`/login?show=${showId}&confId=${confId}&email=${email}`);
                        }
                        else{
                            router.push(`/login?show=${showId}&confId=${confId}`);
                        }
                    }
                    else {
                        router.push(`/login?show=${showId}`);
                    }
                }
                reject(error);
            }else {
                reject(error);
            }
        });
    }

    /**
     * Method will be used to make a get api call.
     * @param  {String} url            Api URL
     * @param  {Object} queryParams    Url query params
     * @param  {Object} auth           Additional request params (headers, etc.)
     * @return {Object}                Promise Object
     */
    get(url, queryParams = {}, auth = {}, addUrlToWatchList = true) {
        auth = this.addApplicationHeaders(auth);
        const queryParamsUrl = `${url}${this.queryParamsBuilder(queryParams)}`;
        return new Promise(this.ajax.bind(this, 'get', queryParamsUrl, {}, addUrlToWatchList, auth));
    }

    /**
     * Method can be used as post/put/update/delete, by passing the method name.
     * @param  {String} url            Api URL
     * @param  {String} post           Request Type [post/put/update]
     * @param  {Object} param          Params to be saved
     * @param  {Object} auth           Additional request params (headers, etc.)
     * @return {Object}                Promise Object
     */
    post(url, method = 'post', param = {}, auth = {}, addUrlToWatchList= true) {
        auth = this.addApplicationHeaders(auth);
        return new Promise(this.ajax.bind(this, method, url, auth, addUrlToWatchList, param))
    }

    delete(url, auth={} , addUrlToWatchList=true ) {
        auth = this.addApplicationHeaders(auth);
        return new Promise(this.ajax.bind(this, 'delete', url, {}, addUrlToWatchList, auth ));
    }
}

export default new Fetch();
